/**
 * PATH: packages/ui/src/components/settings/OrganisationHubTab.tsx
 * PURPOSE: Single Organisation settings surface — Details, Entities, Integrations.
 * WHY: Operator feedback: org details, entity mapping, and integrations belong together.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Building2, Layers, Plug } from 'lucide-react'
import { OrganisationTab } from './OrganisationTab'
import { EntitiesTab, type EntitiesTabProps } from './EntitiesTab'
import { IntegrationsTab } from './IntegrationsTab'
import { EmailAccountsTab } from './EmailAccountsTab'
import { TabbedSettingsPanel } from './TabbedSettingsPanel'
import type { OrganisationPanelId } from './organisation-hub-types'
import type { OrganisationData } from './OrganisationTab'
import type { IntegrationStatus } from './IntegrationsTab'
import type { SettingsApiClient } from './types'
import type { SettingsUser } from '../SettingsModal'

export interface OrganisationHubTabProps {
  initialPanel?: OrganisationPanelId
  organisation?: OrganisationData
  onOrganisationSave?: (data: Partial<OrganisationData>) => Promise<void>
  organisationContent?: ReactNode
  entitiesProps: EntitiesTabProps
  integrations?: IntegrationStatus[]
  isIntegrationsLoading?: boolean
  onIntegrationConnect?: (id: string) => Promise<void>
  onIntegrationDisconnect?: (id: string) => Promise<void>
  onIntegrationSync?: (id: string) => Promise<void>
  integrationsContent?: ReactNode
  apiClient?: SettingsApiClient
  organisationId?: string
  user?: SettingsUser | null
  onOpenIntegrationsPanel?: () => void
}

export function OrganisationHubTab({
  initialPanel = 'details',
  organisation,
  onOrganisationSave,
  organisationContent,
  entitiesProps,
  integrations = [],
  isIntegrationsLoading,
  onIntegrationConnect,
  onIntegrationDisconnect,
  onIntegrationSync,
  integrationsContent,
  apiClient,
  organisationId,
  user,
  onOpenIntegrationsPanel,
}: OrganisationHubTabProps) {
  const [panel, setPanel] = useState<OrganisationPanelId>(initialPanel)

  useEffect(() => {
    setPanel(initialPanel)
  }, [initialPanel])

  const openIntegrations = () => {
    setPanel('integrations')
    onOpenIntegrationsPanel?.()
  }

  const panels = [
    {
      id: 'details' as const,
      label: 'Details',
      icon: Building2,
      content:
        organisationContent ||
        (organisation ? (
          <OrganisationTab
            organisation={organisation}
            onSave={onOrganisationSave}
            editable={!!onOrganisationSave}
          />
        ) : (
          <p className="text-sm text-slate-500">Organisation details are not available.</p>
        )),
    },
    {
      id: 'entities' as const,
      label: 'Entities & mapping',
      icon: Layers,
      content: (
        <EntitiesTab
          {...entitiesProps}
          onConnectXero={entitiesProps.onConnectXero ?? openIntegrations}
        />
      ),
    },
    {
      id: 'integrations' as const,
      label: 'Integrations',
      icon: Plug,
      content: (
        <div className="space-y-10">
          {/*
            Connected mailboxes FIRST. This is the per-mailbox surface the user
            looks for "under Microsoft Outlook" - each mailbox with its own
            status (Active / Reconnect needed), sync toggle, and disconnect.
            It used to render BELOW all the provider cards, so a freshly-added
            second mailbox sat off-screen and looked missing ("I added another
            mailbox and it didn't show"). Surfacing it at the top of the panel
            makes multi-mailbox management discoverable for every tenant.
          */}
          <EmailAccountsTab
            apiClient={apiClient}
            organisationId={organisationId}
            isAdmin={user?.isAdmin || false}
            currentUserId={user?.id ?? null}
          />
          <div className="border-t border-slate-100 dark:border-slate-800 pt-8">
            {integrationsContent || (
              <IntegrationsTab
                integrations={integrations}
                isLoading={isIntegrationsLoading}
                onConnect={onIntegrationConnect}
                onDisconnect={onIntegrationDisconnect}
                onSync={onIntegrationSync}
              />
            )}
          </div>
        </div>
      ),
    },
  ]

  return (
    <TabbedSettingsPanel
      panels={panels}
      activePanel={panel}
      onPanelChange={setPanel}
    />
  )
}
